import React from 'react';
import type {ReactNode} from 'react';
import {Box, Dialog, IconButton} from '@mui/material';
import type {Breakpoint} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type {StagedFile} from '@securspace/securspace-ui-kit';
import {useImagePreviewAction} from './useImagePreviewAction';
import {useFullScreenBreakpoint} from './useFullScreenBreakpoint';
import PreviewStage from './PreviewStage';
import ActionStage from './ActionStage';
import {getPreviewRootSx} from './previewModalStyles';

export interface ImagePreviewAction {
  id: string;
  label: string;
  icon: ReactNode;
  panel: (
    file: StagedFile,
    onComplete: (updated: StagedFile) => void,
    onCancel: () => void,
    onError?: (message: string) => void,
    /** True when the modal is rendering full-screen (see `fullScreenBreakpoint`), so the panel can go edge-to-edge instead of using its default contained layout. */
    fullScreen?: boolean
  ) => ReactNode;
}

export interface ImagePreviewModalProps {
  file: StagedFile;
  actions?: ImagePreviewAction[];
  /** Called when the active action completes. Only reachable when `actions` is non-empty; defaults to a no-op for preview-only callers. */
  onActionComplete?: (updated: StagedFile) => void;
  /** Forwarded as the active action panel's `onError` callback (e.g. a crop failure). */
  onActionError?: (message: string) => void;
  /** Opens the modal directly into the matching action's panel (e.g. `'crop'`) instead of plain preview mode. Must match an id in `actions`. */
  initialActionId?: string;
  /** When set, the dialog renders full-screen at and below this breakpoint (e.g. `'md'`) so mobile users get the full viewport for preview/action interactions. Omit to keep the default contained dialog at every size. */
  fullScreenBreakpoint?: Breakpoint;
  onClose: () => void;
  className?: string;
}

const NO_ACTIONS: ImagePreviewAction[] = [];
const NOOP_ACTION_COMPLETE = () => undefined;

/**
 * Shared image preview shell. The caller owns the current file and open state;
 * this component owns only which optional action panel is active (delegated
 * to `useImagePreviewAction`).
 */
const ImagePreviewModal = (props: ImagePreviewModalProps) => {
  const {
    file,
    actions = NO_ACTIONS,
    onActionComplete = NOOP_ACTION_COMPLETE,
    onActionError,
    initialActionId,
    fullScreenBreakpoint,
    onClose,
    className,
  } = props;
  const {activeAction, selectAction, handleComplete, handleCancel} =
    useImagePreviewAction(actions, onActionComplete, initialActionId ?? null);
  const fullScreen = useFullScreenBreakpoint(fullScreenBreakpoint);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={fullScreen}
      aria-label="Image preview"
      className={className}
    >
      <Box sx={getPreviewRootSx(fullScreen, Boolean(activeAction))}>
        <IconButton
          onClick={onClose}
          aria-label="Close preview"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'common.white',
            bgcolor: 'rgba(0, 0, 0, 0.55)',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
            zIndex: 1,
          }}
        >
          <CloseIcon />
        </IconButton>

        {activeAction ? (
          <ActionStage
            file={file}
            action={activeAction}
            onComplete={handleComplete}
            onCancel={handleCancel}
            onError={onActionError}
            fullScreen={fullScreen}
          />
        ) : (
          <PreviewStage
            file={file}
            actions={actions}
            onSelectAction={selectAction}
            fullScreen={fullScreen}
          />
        )}
      </Box>
    </Dialog>
  );
};

export default ImagePreviewModal;


